import type { ReactElement } from 'react';
import { cn } from '@/lib/utils';
import { isAgentVersionOutdated } from './agentVersion';

interface AgentVersionBadgeProps {
  version: string | null | undefined;
  /**
   * The latest agent release. KIR-5634: never the dashboard build's version
   * (the app version moves on every release, the agent's does not); unknown
   * shows the plain version.
   */
  currentVersion: string | null | undefined;
  className?: string;
}

/**
 * KIR-3911: renders an agent version. Unknown → muted "N/A". Older than the
 * latest agent release → a red pill (same styling as the alerts-count
 * column). Otherwise plain text.
 */
export function AgentVersionBadge({
  version,
  currentVersion,
  className,
}: AgentVersionBadgeProps): ReactElement {
  if (!version) {
    return <span className={cn('text-muted-foreground', className)}>N/A</span>;
  }

  const outdated = isAgentVersionOutdated(version, currentVersion);
  if (!outdated) {
    return <span className={className}>{version}</span>;
  }

  return (
    <span
      title={`Older than the current agent version (${currentVersion})`}
      className={cn(
        'inline-flex items-center justify-center px-2 py-0.5 rounded-md font-medium',
        'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300',
        className
      )}
    >
      {version}
    </span>
  );
}

AgentVersionBadge.displayName = 'AgentVersionBadge';
